/**
 * PromptRenderer — renders a TipTap JSON document as React elements.
 * No HTML string injection. Replaces PromptDisplay for all step types.
 */
import { type ReactNode } from 'react';
import DiagramRenderer from '@/shared/components/DiagramRenderer';
import { MediaAttribution } from '@/shared/components/MediaAttribution';
import { cn } from '@/foundation/utils/cn';
import './PromptRenderer.css';

export interface TiptapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
  text?: string;
}

export interface DiagramJson {
  syntax: string;
  diagramType?: string;
}

export interface MediaItem {
  type: string;
  url: string;
  filename: string;
  role?: string;
  attribution?: {
    required: boolean;
    name: string | null;
    nameUrl: string | null;
    source: string | null;
    sourceUrl: string | null;
  } | null;
}

export interface PromptRendererProps {
  doc: TiptapNode;
  renderBlank?: (blankId: string) => ReactNode;
  /** Media from the API (already signed). Array of media items. */
  media?: MediaItem[] | null;
  diagramJson?: DiagramJson | null;
  /** Position of visual element (diagram or media) relative to prompt. Default: 'above'. */
  visualPosition?: 'above' | 'below';
  className?: string;
}

/** Wraps a text node in mark elements (strong, em, u) based on the marks array. */
function TextWithMarks({
  text,
  marks,
}: {
  text: string;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
}): ReactNode {
  if (!marks || marks.length === 0) {
    return text;
  }

  let node: ReactNode = text;
  // Apply marks from innermost to outermost (last mark wraps first)
  for (let i = marks.length - 1; i >= 0; i--) {
    const mark = marks[i];
    switch (mark.type) {
      case 'bold':
        node = <strong>{node}</strong>;
        break;
      case 'italic':
        node = <em>{node}</em>;
        break;
      case 'underline':
        node = <u>{node}</u>;
        break;
      case 'strike':
        node = <s>{node}</s>;
        break;
      case 'code':
        node = <code>{node}</code>;
        break;
      default:
        break;
    }
  }
  return node;
}

/** Recursively renders a TipTap node as React elements. */
function renderNode(
  node: TiptapNode,
  key: string | number,
  renderBlank?: (blankId: string) => ReactNode
): ReactNode {
  const children = node.content
    ? node.content.map((child, i) => renderNode(child, i, renderBlank))
    : null;

  switch (node.type) {
    case 'doc':
      return <>{children}</>;

    case 'paragraph':
      return <p key={key}>{children}</p>;

    case 'text':
      return <TextWithMarks key={key} text={node.text ?? ''} marks={node.marks} />;

    case 'hardBreak':
      return <br key={key} />;

    case 'bulletList':
      return <ul key={key}>{children}</ul>;

    case 'orderedList':
      return <ol key={key}>{children}</ol>;

    case 'listItem':
      return <li key={key}>{children}</li>;

    case 'table':
      return (
        <table key={key}>
          <tbody>{children}</tbody>
        </table>
      );

    case 'tableRow':
      return <tr key={key}>{children}</tr>;

    case 'tableHeader': {
      const colspan = typeof node.attrs?.colspan === 'number' ? node.attrs.colspan : undefined;
      const rowspan = typeof node.attrs?.rowspan === 'number' ? node.attrs.rowspan : undefined;
      return (
        <th key={key} colSpan={colspan} rowSpan={rowspan}>
          {children}
        </th>
      );
    }

    case 'tableCell': {
      const colspan = typeof node.attrs?.colspan === 'number' ? node.attrs.colspan : undefined;
      const rowspan = typeof node.attrs?.rowspan === 'number' ? node.attrs.rowspan : undefined;
      return (
        <td key={key} colSpan={colspan} rowSpan={rowspan}>
          {children}
        </td>
      );
    }

    case 'clozeBlank': {
      const blankId = typeof node.attrs?.id === 'string' ? node.attrs.id : '';
      if (renderBlank) {
        return <span key={key}>{renderBlank(blankId)}</span>;
      }
      return (
        <span key={key} className="cloze-blank">
          ___
        </span>
      );
    }

    default:
      return children ? <>{children}</> : null;
  }
}

/**
 * Walks a TipTap JSON tree recursively and returns cloze blank IDs in document order.
 * Used by ClozeStep to determine blank ordering without regex HTML parsing.
 */
export function extractBlankIds(node: TiptapNode): string[] {
  const ids: string[] = [];

  function walk(n: TiptapNode): void {
    if (n.type === 'clozeBlank' && typeof n.attrs?.id === 'string') {
      ids.push(n.attrs.id);
    }
    if (n.content) {
      for (const child of n.content) {
        walk(child);
      }
    }
  }

  walk(node);
  return ids;
}

/** Render media element based on type. */
function renderMedia(media: { type: string; url: string; filename: string }) {
  switch (media.type) {
    case 'image':
      return (
        <img
          src={media.url}
          alt={media.filename}
          className="max-h-64 max-w-full rounded-lg object-contain"
        />
      );
    case 'video':
      return (
        <video
          src={media.url}
          controls
          preload="metadata"
          className="max-h-80 max-w-full rounded-lg"
        >
          <track kind="captions" />
        </video>
      );
    case 'audio':
      return (
        <audio src={media.url} controls preload="metadata" className="w-full max-w-md">
          <track kind="captions" />
        </audio>
      );
    default:
      return null;
  }
}

/** Render media element with attribution text below. */
function renderMediaWithAttribution(item: MediaItem) {
  const el = renderMedia(item);
  if (!el) return null;
  return (
    <div>
      {el}
      <MediaAttribution attribution={item.attribution ?? null} />
    </div>
  );
}

/**
 * Renders a TipTap JSON document as React elements with optional media/diagram.
 * Replaces the HTML-string-based PromptDisplay component.
 */
export function PromptRenderer({
  doc,
  renderBlank,
  media,
  diagramJson,
  visualPosition = 'above',
  className,
}: PromptRendererProps) {
  const diagramEl = diagramJson?.syntax ? (
    <DiagramRenderer syntax={diagramJson.syntax} className="rounded-lg" />
  ) : null;

  const mediaItems = media ?? [];
  const primaryImage = mediaItems.find(
    (m) => m.type === 'image' && (!m.role || m.role === 'primary_image')
  );
  const mediaEl = !diagramEl && primaryImage ? renderMediaWithAttribution(primaryImage) : null;

  const visualEl = diagramEl ?? mediaEl;

  const promptEl = (
    <div
      className={cn('f-prompt-renderer', className)}
      style={{
        fontSize: 'var(--text-2xl, var(--f-text-2xl))',
        lineHeight: 'var(--leading-normal, var(--f-leading-normal))',
        letterSpacing: 'var(--tracking-tight, var(--f-tracking-tight))',
        fontFamily: 'var(--font-sans, var(--f-font-sans))',
        fontWeight: 500,
        color: 'var(--color-text, var(--f-fg))',
      }}
    >
      {renderNode(doc, 'root', renderBlank)}
    </div>
  );

  return (
    <div
      className="flex flex-col gap-4"
      style={{
        padding: 'var(--space-6, var(--f-space-6)) 0',
        fontFamily: 'var(--font-sans, var(--f-font-sans))',
      }}
    >
      {visualPosition === 'above' ? (
        <>
          {visualEl}
          {promptEl}
        </>
      ) : (
        <>
          {promptEl}
          {visualEl}
        </>
      )}
    </div>
  );
}
